import { useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, CalendarDays } from "lucide-react";
import { api } from "@/lib/api";
import { openCalendly } from "@/lib/calendly";

const TIME_SLOTS = [
  "09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00", "17:00",
];

function nextNDays(n) {
  const out = [];
  const today = new Date();
  let added = 0;
  let i = 1;
  while (added < n) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const day = d.getDay();
    if (day !== 0 && day !== 6) {
      out.push(d);
      added++;
    }
    i++;
  }
  return out;
}

function fmtDateValue(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
}

const initial = { name: "", email: "", message: "" };

export default function BookMeeting() {
  const days = useMemo(() => nextNDays(10), []);
  const [form, setForm] = useState(initial);
  const [date, setDate] = useState(fmtDateValue(days[0]));
  const [time, setTime] = useState("10:00");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    if (!date || !time) {
      toast.error("Pick a date and time.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        name: form.name.trim(),
        email: form.email.trim(),
        preferred_date: date,
        preferred_time: time,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Booking request sent.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || err?.message || "Something went wrong.";
      toast.error(typeof detail === "string" ? detail : "Booking failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="book-meeting-section"
      className="p-8 md:p-12 lg:p-16 bg-[#F4F5F7] relative overflow-hidden"
    >
      <div className="relative max-w-xl">
        <div data-testid="book-meeting-section-label" className="font-mono-label text-[11px] text-[#0F1117] mb-6">
          / Book a meeting
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight font-extrabold text-[#0F1117] text-balance mb-4">
          Get on the calendar.
        </h2>
        <p className="text-[15px] text-[#52555B] leading-relaxed mb-8">
          {"Pick a slot that works. 30 minutes, on Google Meet."}
        </p>

        <button
          type="button"
          data-testid="book-calendly-btn"
          onClick={openCalendly}
          className="group chrome-btn-dark chrome-btn-dark-glow inline-flex items-center gap-2.5 font-mono-label text-[12px] px-7 py-4 mb-10"
        >
          <span className="live-dot" aria-hidden />
          Book instantly with Calendly
          <ArrowUpRight
            size={16}
            className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </button>

        <div className="font-mono-label text-[10px] tracking-[0.18em] uppercase text-[#71747B] mb-6">
          {"Or send a request — we'll confirm via email"}
        </div>

        {!done ? (
          <form onSubmit={onSubmit} data-testid="book-meeting-form" className="space-y-6">
            <Field label="Name *" htmlFor="bk-name">
              <input
                id="bk-name"
                data-testid="bk-name-input"
                type="text"
                autoComplete="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
                placeholder="Jane Doe"
              />
            </Field>

            <Field label="Email *" htmlFor="bk-email">
              <input
                id="bk-email"
                data-testid="bk-email-input"
                type="email"
                autoComplete="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputCls}
                placeholder="you@company.com"
              />
            </Field>

            <fieldset className="min-w-0" style={{ minWidth: 0 }}>
              <legend className="font-mono-label text-[11px] text-[#5F6168] mb-3 block">
                Preferred date *
              </legend>
              <div className="w-full min-w-0 flex gap-px overflow-x-auto pb-1 bg-[#D2D5DA] border hairline">
                {days.map((d) => {
                  const v = fmtDateValue(d);
                  const active = v === date;
                  return (
                    <button
                      key={v}
                      type="button"
                      data-testid={`date-${v}`}
                      onClick={() => setDate(v)}
                      className={`chrome-tile shrink-0 px-4 py-3 text-center min-w-[88px] ${
                        active ? "chrome-tile-active" : "text-[#0F1117]"
                      }`}
                    >
                      <div className="font-mono-label text-[10px] opacity-80">
                        {d.toLocaleDateString("en-US", { weekday: "short" })}
                      </div>
                      <div className="font-display text-xl font-bold mt-1">
                        {d.getDate()}
                      </div>
                      <div className="font-mono-label text-[10px] opacity-80 mt-0.5">
                        {d.toLocaleDateString("en-US", { month: "short" })}
                      </div>
                    </button>
                  );
                })}
              </div>
            </fieldset>

            <fieldset>
              <div className="flex flex-col sm:flex-row sm:items-baseline justify-between gap-2 sm:gap-3 mb-3">
                <legend className="font-mono-label text-[11px] text-[#5F6168]">
                  Preferred time *
                </legend>
                <span
                  data-testid="timezone-indicator"
                  className="font-mono-label text-[9.5px] tracking-[0.14em] text-[#0F1117] inline-flex items-center gap-1.5"
                >
                  <span className="inline-block w-1 h-1 rounded-full bg-[#22C55E]" />
                  ALL TIMES EASTERN (ET) · UTC-5
                </span>
              </div>
              <div className="grid grid-cols-4 gap-px bg-[#D2D5DA] border hairline">
                {TIME_SLOTS.map((t) => {
                  const active = t === time;
                  return (
                    <button
                      key={t}
                      type="button"
                      data-testid={`time-${t}`}
                      onClick={() => setTime(t)}
                      className={`chrome-tile px-3 py-3 font-mono-label text-[11px] ${
                        active ? "chrome-tile-active" : "text-[#0F1117]"
                      }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 font-mono-label text-[9px] text-[#71747B] leading-relaxed">
                International? Note your local time in the message below and we will
                convert and confirm by email.
              </p>
            </fieldset>

            <Field label="Message" htmlFor="bk-msg">
              <textarea
                id="bk-msg"
                data-testid="bk-message-input"
                rows={3}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${inputCls} resize-none`}
                placeholder="Optional — what do you want to discuss?"
              />
            </Field>

            <button
              type="submit"
              disabled={submitting}
              data-testid="bk-submit-btn"
              className="chrome-btn-dark w-full inline-flex items-center justify-center gap-2 font-mono-label text-[12px] px-6 py-4 disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Request meeting"}
              {!submitting && <ArrowUpRight size={16} />}
            </button>
          </form>
        ) : (
          <div data-testid="bk-thank-you" className="fade-up">
            <div className="font-mono-label text-[11px] text-[#0F1117] mb-4">
              / Booking received
            </div>
            <h3 className="font-display text-3xl sm:text-4xl font-extrabold text-[#0F1117] tracking-tight mb-3">
              {`You're on the list${form.name ? `, ${form.name.split(" ")[0]}` : ""}.`}
            </h3>
            <p className="text-[15px] text-[#52555B] leading-relaxed mb-8 max-w-md">
              {"We've sent a confirmation. Someone from DCYB will email back to finalize the slot."}
            </p>
            <div className="chrome-surface p-6 inline-flex items-center gap-4">
              <CalendarDays size={22} className="text-[#0F1117]" />
              <div>
                <div className="font-mono-label text-[10px] text-[#5F6168]">Requested</div>
                <div className="font-display text-lg font-bold text-[#0F1117]">
                  {date} · {time}
                </div>
              </div>
            </div>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => {
                  setDone(false);
                  setForm(initial);
                }}
                data-testid="bk-reset-btn"
                className="font-mono-label text-[11px] text-[#5F6168] hover:text-[#0F1117] transition-colors"
              >
                ← Request another
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="font-mono-label text-[11px] text-[#5F6168] mb-2 block">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputCls =
  "chrome-surface w-full rounded-none px-4 py-3.5 text-[15px] text-[#0F1117] focus:outline-none focus:border-[#0F1117] focus:ring-1 focus:ring-[#0F1117] transition-colors placeholder:text-[#9498A0]";
